// Heatmap "Utilización de la capacidad instalada" — componente React/Tremor.
// Filas = franjas horarias, columnas = meses. El color de cada celda representa
// la utilización promedio diaria (%). Reusa la paleta corporativa del tablero.
import { useMemo } from "react";
import { Card, Title, Text } from "@tremor/react";
import { colorFor } from "../lib/heatmapLogic";

export interface UtilizacionCelda {
  franja: string;
  mm: number;
  mes_label: string;
  adultos: number | null;
  capacidad_diaria: number | null;
  utilizacion_pct: number | null;
}

export interface UtilizacionData {
  restaurant_id: string;
  anio: number;
  franjas: string[];
  meses: { mm: number; label: string }[];
  capacidad_sillas: number;
  celdas: UtilizacionCelda[];
}

// ── Componente ──────────────────────────────────────────
export default function HeatmapUtilizacion({ data }: { data: UtilizacionData }) {
  const h = useMemo(() => {
    // Orden cronológico de franjas (0-based, formato HH:MM)
    const franjaMin = (f: string) => {
      const m = f.match(/^(\d+):(\d+)$/);
      return m ? parseInt(m[0].replace(":", ""), 10) : 99999;
    };
    const franjas = [...data.franjas].sort((a, b) => franjaMin(a) - franjaMin(b));

    const matrix: Record<string, Record<number, UtilizacionCelda | undefined>> = {};
    for (const celda of data.celdas) {
      if (!matrix[celda.franja]) matrix[celda.franja] = {};
      matrix[celda.franja][celda.mm] = celda;
    }

    // Min/max para la escala de color (solo celdas con dato)
    const vals = data.celdas
      .map((c) => c.utilizacion_pct)
      .filter((v): v is number => v != null);
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 100;

    return { franjas, matrix, min, max };
  }, [data]);

  return (
    <Card className="!p-3 h-full">
      <Title className="!text-base">Utilización de la capacidad instalada</Title>
      <Text className="!text-xs">
        Comensales ÷ (Capacidad de asientos × N° servicios) × 100 · % por franja y mes
        {data.capacidad_sillas ? ` · ${data.capacidad_sillas} asientos` : ""}
      </Text>

      <div className="mt-3 flex gap-4">
        {/* Matriz */}
        <div className="flex-1 overflow-x-auto">
          <table className="w-full border-collapse" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th className="text-left text-[11px] font-medium text-tremor-content p-1 whitespace-nowrap">Franja</th>
                {data.meses.map((m) => (
                  <th key={m.mm} className="text-center text-[11px] font-medium text-tremor-content p-1 whitespace-nowrap">
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {h.franjas.map((f) => (
                <tr key={f}>
                  <td className="text-left text-[11px] font-medium text-tremor-content-emphasis p-1 whitespace-nowrap">
                    {f}
                  </td>
                  {data.meses.map((m) => {
                    const cell = h.matrix[f]?.[m.mm];
                    const pct = cell?.utilizacion_pct ?? null;
                    const { bg, fg } = colorFor(pct, h.min, h.max);
                    return (
                      <td key={m.mm} className="p-0.5">
                        <div
                          className="flex items-center justify-center rounded-sm"
                          style={{
                            background: bg,
                            color: fg,
                            height: 28,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                          title={`${f} · ${m.label}: ${pct != null ? `${pct}%` : "s/d"}${
                            cell?.adultos != null ? ` (${cell.adultos} comensales)` : ""
                          }`}
                        >
                          {pct != null ? `${pct}%` : "—"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Leyenda vertical */}
        <div className="flex flex-col items-center justify-center shrink-0" style={{ width: 54 }}>
          <span className="text-[10px] text-tremor-content mb-1">Mayor</span>
          <div
            className="rounded-sm"
            style={{ width: 16, height: 120, background: "linear-gradient(to bottom, #15335B, #6F91B3)" }}
          />
          <span className="text-[10px] text-tremor-content mt-1">Menor</span>
        </div>
      </div>
    </Card>
  );
}
